import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../integrations/supabase/client';

const { FiTruck, FiShield, FiTrendingUp } = FiIcons;

const NextSupplySection = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [content, setContent] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      const { data } = await supabase.from('page_content').select('*').eq('page', 'home').eq('section', 'next_supply');
      if (data) {
        const contentMap = {};
        data.forEach(item => {
          contentMap[item.key] = isRTL ? (item.content_ar || item.content_en) : item.content_en;
        });
        setContent(contentMap);
      }
    };
    fetchContent();
  }, [isRTL]);

  const getText = (key, fallbackKey) => content?.[key] || t(fallbackKey);

  const capabilities = [
    { icon: FiTruck, title: t('nextSupply.network'), description: t('nextSupply.networkText') },
    { icon: FiShield, title: t('nextSupply.commitment'), description: t('nextSupply.commitmentText') },
    { icon: FiTrendingUp, title: t('nextSupply.expertise'), description: t('nextSupply.expertiseText') }
  ];

  const achievements = [
    { number: "500+", label: isRTL ? "شريك تجزئة" : "Retail Partners" },
    { number: "27", label: isRTL ? "محافظة مغطاة" : "Governorates" },
    { number: "15+", label: isRTL ? "سنوات خبرة" : "Years Experience" },
    { number: "#1", label: isRTL ? "توزيع القهوة" : "Coffee Distribution" }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-green-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className={`flex items-center justify-center space-x-6 mb-8 ${isRTL ? 'space-x-reverse' : ''}`}>
            <div className="relative group">
              <div className="absolute inset-0 bg-white/80 rounded-full backdrop-blur-sm"></div>
              <div className="relative bg-white/90 rounded-full p-3 shadow-lg backdrop-blur-sm border border-white/50">
                <img 
                  src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759920909971-NEXT%20SUPPLY%20PNG.png" 
                  alt="Next Supply Logo" 
                  className="w-24 h-24 object-contain relative z-10"
                />
              </div>
            </div>
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <h2 className="text-4xl font-bold text-green-800">
                {getText('title', 'nextSupply.title')}
              </h2>
              <p className="text-green-600 font-medium text-xl">
                {getText('subtitle', 'nextSupply.subtitle')}
              </p>
            </div>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
             {getText('description', 'nextSupply.description')}
          </p>
        </motion.div>

        {/* ... (Keep capabilities and achievements static for now as they are structural) ... */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {capabilities.map((capability, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              className="bg-white p-8 rounded-2xl shadow-lg border border-green-100 group"
            >
              <div className="bg-gradient-to-br from-green-600 to-green-700 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <SafeIcon icon={capability.icon} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-green-800 mb-3">{capability.title}</h3>
              <p className="text-gray-600">{capability.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default NextSupplySection;